<template>
  <div class="mx-3">
    <v-row>
      <v-spacer />

      <v-col
        class="py-0"
        cols="12"
        lg="4"          
        md="12"
        sm="12"
        xl="3"
      >
        <v-autocomplete
          v-model="claimType"
          :items="claimTypes"
          multiple
          item-text="name"
          item-value="id"
          hide-no-data
          chips
          small-chips
          deletable-chips
          hide-selected
          clearable
          label="Claim Type Filter"
          placeholder="Choose claim type"
          @change="loadData"
        />
      </v-col>

      <v-col
        class="py-0"
        cols="12"
        lg="2"          
        md="12"
        sm="12"
        xl="1"
      >
        <v-autocomplete
          v-model="selectedYear"
          :items="claimYears"
          item-text="name"
          item-value="name"
          hide-no-data
          hide-selected
          label="Year Filter"
          placeholder="Choose year"
          @change="loadData"
        />
      </v-col>

      <v-col
        class="py-0"
        cols="12"
        lg="3"          
        md="12"
        sm="12"
        xl="2"
      >
        <v-text-field
          v-model="searchInput"
          placeholder="Key in name/department"
          append-icon="search"
          label="Search"
          hide-no-data
          hide-selected
          autofocus
          clearable 
        />
      </v-col>
    </v-row>

    <v-data-table
      hide-default-footer
      :headers="headers"
      :items="entitlements"
      :loading="tableLoading"
    >
      <template v-slot:item.assignable="{ item }">
        <div style="max-width:250px">
          <span>
            {{ (item.assignable.person || {}).name ? (item.assignable.person || {}).name : item.assignable.name }}
          </span>
          <setup-claims-claim-entitlement-dialog-list-adjustment
            v-if="item.claim_entitlement_adjustments.length > 0 && $hasPermissionTo(['view claim entitlement adjustments'])"
            :entitlement="item"
          />
        </div>
      </template>

      <template v-slot:item.entitled_amount="{ item }">
        <span 
          v-if="item.claim_type.has_limit"
          style="max-width:200px"
        >
          RM {{ currencyFormatting(item.entitled_amount) }} / {{ item.credit_unit }}
        </span>
        <span 
          v-else
          style="max-width:200px"
        >
          <v-icon>
            mdi-infinity
          </v-icon>
        </span>
      </template>
      
      <template v-slot:item.current_available="{ item }">
        <span
          v-if="item.claim_type.has_limit"
        >
          {{ currencyFormatting(item.current_available) }}
        </span>
        <span 
          v-else
          style="max-width:200px"
        >
          <v-icon>
            mdi-infinity
          </v-icon>
        </span>
      </template>
      
      <template v-slot:item.adjusted_amount="{ item }">
        <span v-if="item.claim_type.has_limit">
          {{ currencyFormatting(item.adjusted_amount) }}
        </span>
        <span 
          v-else
          style="max-width:200px"
        >
          -
        </span>
        <setup-claims-claim-entitlement-dialog-adjustment
          v-if="$hasPermissionTo(['adjust claim entitlements'])"
          :claim-entitlement="item"
          @updated="loadData"
        />
      </template>

      <template v-slot:item.claimed_amount="{ item }">
        <span
          :class="[
            item.claim_type.has_limit == 1 && item.claim_type.allow_exceed_limit == 1 && calculateClaimedAmount(item) > (item.current_available + item.adjusted_amount)
              ? 'red--text text--accent-3'
              : ''
          ]"
        >
          {{ currencyFormatting(calculateClaimedAmount(item)) }}
          <setup-claims-claim-entitlement-dialog-view-claim-requests
            v-if="$hasPermissionTo(['view claim requests']) && item.entries.length > 0 && item.claimed_amount > 0"
            :entitlement="item"
          />
        </span>
      </template>

      <template v-slot:item.balance="{ item }">
        <span v-if="item.claim_type.has_limit">
          {{ currencyFormatting((item.current_available + item.adjusted_amount) - calculateClaimedAmount(item)) }}
        </span>
        <span 
          v-else
          style="max-width:200px"
        >
          -
        </span>
      </template>

      <template v-slot:item.action="{ item }">
        <span
          style="display:inline-block;width:80px"
        >
          <setup-claims-claim-entitlement-dialog-delete
            v-if="$hasPermissionTo(['delete claim entitlements'])"
            :claim-entitlement="item"
            :claimed-amount="calculateClaimedAmount(item)"
            @deleted="loadData"
          />
        </span>
      </template>
    </v-data-table>
    <v-divider class="ml-0 mr-0" />
    <v-container class="text-right">
      <v-btn
        icon
        color="primary"
        :disabled="currentPage === 1"
        @click="previousPage"
      >
        <v-icon>mdi-chevron-left</v-icon>
      </v-btn>
      <span class="pagination">{{ from }}-{{ to }} of {{ total }}</span>
      <v-btn
        icon
        color="primary"
        :disabled="currentPage === lastPage"
        @click="nextPage"
      >
        <v-icon>mdi-chevron-right</v-icon>
      </v-btn>
    </v-container>
  </div>
</template>

<script>
  export default {
    components: {
      SetupClaimsClaimEntitlementDialogDelete: () => import('@/components/hris/setup/claims/claim-entitlement/DialogDelete'),
      SetupClaimsClaimEntitlementDialogAdjustment: () => import('@/components/hris/setup/claims/claim-entitlement/DialogAdjustment'),
      SetupClaimsClaimEntitlementDialogListAdjustment: () => import('@/components/hris/setup/claims/claim-entitlement/DialogListAdjustment'),
      SetupClaimsClaimEntitlementDialogViewClaimRequests: () => import('@/components/hris/setup/claims/claim-entitlement/DialogViewClaimRequests'),
    },
    data () {
      return {
        selectedYear: parseInt(this.$moment().format('YYYY')),
        headers: [
          {
            text: 'Name/Department',
            align: 'left',
            sortable: false,
            value: 'assignable',
          },
          {
            text: 'Claim Type',
            align: 'left',
            sortable: false,
            value: 'claim_type.name',
          },
          {
            text: 'Start Date',
            align: 'left',
            sortable: false,
            value: 'start_date',
          },
          {
            text: 'End Date',
            align: 'left',
            sortable: false,
            value: 'end_date',
          },
          {
            text: 'Entitled Amount',
            align: 'right',
            sortable: false,
            value: 'entitled_amount',
          },
          {
            text: 'Entitlement (RM)',
            align: 'right',
            sortable: false,
            value: 'current_available',
          },
          {
            text: 'Adjusted (RM)',
            align: 'right',
            sortable: false,
            value: 'adjusted_amount',
          },
          {
            text: 'Claimed (RM)',
            align: 'right',
            sortable: false,
            value: 'claimed_amount',
          },
          {
            text: 'Balance (RM)',
            align: 'right',
            sortable: false,
            value: 'balance',
          },
          {
            text: '',
            sortable: false,
            value: 'action',
          },
        ],  
        searchInput: '',
        claimType: null,
        tableLoading: false,
        entitlements: [],
        claimTypes: [],
        claimYears: [],
        total: 0,
        from: 0,
        to: 0,
        lastPage: 0, 
        currentPage: 1,
      }
    },
    watch: {
      searchInput (val) {
        clearTimeout(this._searchTimerId)
        this._searchTimerId = setTimeout(() => {
          this.tableLoading = true
          this.loadData()
        }, 500) /* 500ms throttle */
      },
    },
    created () {
      this.loadClaimType()
      this.loadClaimEntitlementYear()
      this.loadData()
    },
    methods: {
      loadClaimType () {
        this.$api.claimTypes().list()
          .then(res => {
            this.claimTypes = _.orderBy(res.data, ['name'], ['asc'])
          })
      },
      loadClaimEntitlementYear () {
        this.$api.claimEntitlements().listYear()
          .then(res => {
            this.claimYears = res.data
          })
      },
      calculateClaimedAmount (item) {
        if (!item.entries || item.entries.length === 0) {
          return 0
        }

        if (item.credit_unit === 'day') {
          const entry = item.entries[0] || {}
          let value = item.claimed_amount || 0

          if (entry.claim_request_entry_dates && entry.claim_request_entry_dates.length > 0) {
            const claimRequestEntryDate = entry.claim_request_entry_dates.find(
              d => d.date === item.start_date
            )

            if (claimRequestEntryDate && claimRequestEntryDate.amount_to_deduct !== null) {
              value = claimRequestEntryDate.amount_to_deduct
            }
          }

          return parseFloat(value)
        }

        return item.entries.reduce((total, entry) => {
          const quantity = parseFloat(entry.quantity || 0)
          const amount = parseFloat(entry.amount || 0)
          return total + quantity * amount
        }, 0)
      },
      currencyFormatting (item) {
        let value = (Math.round((item / 100) * 100) / 100).toFixed(2)
        return this.thousandSeparator(value)
      },
      thousandSeparator (amount) {
        if (amount !== '' || amount !== undefined || amount !== 0 || amount !== '0' || amount !== null) {
          return amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
        } else {
          return amount
        }
      },
      loadData () {
        this.tableLoading = true

        this.$api.claimEntitlements().list('', this.prepareFormData())
          .then(res => {
            this.setPaginationDetails(res.data)
            this.entitlements = res.data.data
            this.$emit('loaded')
            this.tableLoading = false
          })
      },
      setPaginationDetails (page) {
        this.total = page.total
        this.from = page.from
        this.to = page.to
        this.lastPage = page.last_page
        this.currentPage = page.current_page
      },
      nextPage () {
        this.tableLoading = true

        this.$api.claimEntitlements().list('?page=' + (this.currentPage + 1), this.prepareFormData())
          .then(res => {
            this.entitlements = res.data.data
            this.setPaginationDetails(res.data)
    
            this.tableLoading = false
          })
      },
      previousPage () {
        this.tableLoading = true

        this.$api.claimEntitlements().list('?page=' + (this.currentPage - 1), this.prepareFormData())
          .then(res => {
            this.entitlements = res.data.data
            this.setPaginationDetails(res.data)
    
            this.tableLoading = false
          })
      },
      prepareFormData () {
        return {
          searchInput: this.searchInput,
          year: this.selectedYear,
          claimType: this.claimType,
        }
      },
    },
  }
</script>

<style scoped>
  ::v-deep .v-toolbar__content{
    margin-left:0px !important;
  }

  ::v-deep .v-table__overflow{
    overflow-x: hidden;
  }
</style>
